// =====================================================================
// commande-prete/SurDalle.jsx — panneau « 🧱 Commandes sur dalle » à gauche de la page (27-10-2026)
// =====================================================================
// Affiché seulement avec « Administrer les commandes prêtes » (la page décide ; le serveur refuse sinon).
// Temps sur dalle = du 1er mail « prête » au chargement de la dernière palette (ou jusqu'à maintenant).
// « Encore sur dalle » : toutes dates ; « parties » : dernier chargement dans la période choisie.
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { readyOrders, errMsg } from '../../services/api.js';
import { enregistrerReponse, messageDeBlob } from '../../lib/telechargement.js';
import { dateHeureBxl } from './dates.js';
import { DEBUT_MESURE, aujourdhuiBxl, erreurPeriode, auMax } from './tempsEncodage.js';
import { PERIODES_DALLE, bornesDalle, joursTexte, joursEntiers, couleurAge, texteRestant } from './surDalle.js';

const carte = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem' };
const puce = (actif) => ({ border: `1px solid ${actif ? '#5b2333' : '#cbd5e1'}`, background: actif ? '#5b2333' : '#fff', color: actif ? '#fff' : '#334155',
  borderRadius: 6, padding: '4px 8px', fontSize: '0.78rem', cursor: 'pointer' });
const cellN = { textAlign: 'right', whiteSpace: 'nowrap', padding: '6px 4px' };
const th = { padding: '6px 4px', textAlign: 'left', verticalAlign: 'bottom' };
// en-têtes des colonnes de chiffres : ils passent à la ligne (jamais plus larges que le panneau, sinon coupés au bord)
const thN = { ...th, textAlign: 'right' };
const age = (minutes) => ({ background: couleurAge(minutes), color: '#fff', borderRadius: 10, padding: '1px 8px', fontWeight: 700, fontSize: '0.74rem' });

export default function SurDalle() {
  const [cle, setCle] = useState('30j');
  const [libre, setLibre] = useState({ du: DEBUT_MESURE, au: aujourdhuiBxl() });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [client, setClient] = useState(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  const derniere = useRef('');

  const periode = cle === 'libre' ? libre : bornesDalle(cle);
  const invalide = cle === 'libre' ? erreurPeriode(libre.du, libre.au) : null;

  useEffect(() => {
    const k = `${periode.du}|${periode.au}|${invalide || ''}`;
    if (k !== derniere.current) { derniere.current = k; setData(null); setErr(''); setClient(null); }
    if (invalide) return undefined;
    let actuel = true;
    readyOrders.statsDalle(periode.du, periode.au)
      .then((r) => { if (actuel) setData(r); })
      .catch((e) => { if (actuel) setErr(errMsg(e)); });
    return () => { actuel = false; };
  }, [periode.du, periode.au, invalide]);

  const exporter = async () => {
    if (exportEnCours || !data) return;   // pas « disabled » : le garde de focus de la page y verrait un focus tombé
    setExportEnCours(true); setErr('');
    try { enregistrerReponse(await readyOrders.exportDalle(data.du, data.au), 'commandes-pretes-sur-dalle.xlsx'); }
    catch (e) { setErr(await messageDeBlob(e)); }
    finally { setExportEnCours(false); }
  };

  const clients = data ? data.clients.filter((c) => c.surDalle > 0) : [];
  const clientActif = client && clients.some((c) => c.cle === client) ? client : null;
  const liste = data ? data.surDalle.liste.filter((l) => !clientActif || l.cleClient === clientActif) : [];
  const maxTranche = data ? Math.max(1, ...data.tranches.map((t) => Math.max(t.surDalle, t.parties))) : 1;

  return (
    <aside id="surDalle" style={carte} aria-label="Commandes sur dalle">
      <h2 style={{ margin: 0, fontSize: '1.05rem' }}>🧱 Commandes sur dalle</h2>
      <p style={{ margin: '4px 0 10px', fontSize: '0.8rem', color: '#64748b' }}>Du 1<sup>er</sup> mail « prête » au chargement de la dernière palette ou du dernier colis.</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {PERIODES_DALLE.map((p) => <button key={p.cle} type="button" style={puce(cle === p.cle)} onClick={() => setCle(p.cle)}>{p.libelle}</button>)}
        <button type="button" style={puce(cle === 'libre')} onClick={() => setCle('libre')}>📅 Du … au …</button>
      </div>
      {cle === 'libre' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, fontSize: '0.8rem' }}>
          <label>Du <input type="date" min={DEBUT_MESURE} max={libre.au || undefined} value={libre.du} onChange={(e) => setLibre((v) => ({ ...v, du: e.target.value }))} /></label>
          <label>au <input type="date" min={libre.du || DEBUT_MESURE} max={auMax(libre.du)} value={libre.au} onChange={(e) => setLibre((v) => ({ ...v, au: e.target.value }))} /></label>
        </div>
      )}
      {invalide && <div role="alert" style={{ color: '#a6342b', fontSize: '0.82rem', marginBottom: 8 }}>{invalide}</div>}
      {err && <div role="alert" style={{ color: '#a6342b', fontSize: '0.82rem', marginBottom: 8 }}>{err}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
            {[[data.surDalle.commandes, 'commandes encore sur dalle'], [data.surDalle.palettes, 'palettes sur dalle'],
              [joursTexte(data.surDalle.plusAncienneMinutes), 'la plus ancienne'], [joursTexte(data.parties.medianeMinutes), 'médiane avant départ']].map(([v, l]) => (
              <div key={l} style={{ background: '#f8fafc', borderRadius: 8, padding: 8, fontSize: '0.7rem', color: '#64748b' }}>
                <b style={{ display: 'block', fontSize: '1.1rem', color: '#1e293b' }}>{v}</b>{l}
              </div>
            ))}
          </div>

          <h3 style={{ margin: '10px 0 6px', fontSize: '0.88rem' }}>Combien de temps les commandes restent sur dalle</h3>
          {data.tranches.map((t) => (
            <div key={t.libelle} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 8, alignItems: 'center', margin: '4px 0', fontSize: '0.75rem' }}>
              <b>{t.libelle}</b>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ height: 13, width: `${(t.surDalle / maxTranche) * 100}%`, minWidth: t.surDalle ? 4 : 0, background: couleurAge(t.de * 1440 + 1), borderRadius: 3 }} />
                  <span style={{ color: '#475569' }}>{t.surDalle} sur dalle ({t.palettesSurDalle} pal.)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <div style={{ height: 9, width: `${(t.parties / maxTranche) * 100}%`, minWidth: t.parties ? 4 : 0, background: '#cbd5e1', borderRadius: 3 }} />
                  <span style={{ color: '#94a3b8' }}>{t.parties} parties</span>
                </div>
              </div>
            </div>
          ))}

          <h3 style={{ margin: '14px 0 4px', fontSize: '0.88rem' }}>Jour par jour</h3>
          <p style={{ margin: '0 0 6px', fontSize: '0.72rem', color: '#64748b' }}>Encore sur dalle : depuis combien de jours elles attendent · parties : combien de jours elles sont restées.</p>
          {data.jours.length === 0 ? <p style={{ fontSize: '0.8rem', color: '#64748b' }}>Aucune commande sur cette période.</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead><tr style={{ color: '#64748b', fontSize: '0.7rem' }}>
                <th style={th}>Jours sur dalle</th><th style={thN}>Total</th><th style={thN}>Encore sur dalle</th><th style={thN}>Parties</th><th style={thN}>Palettes sur dalle</th>
              </tr></thead>
              <tbody>
                {data.jours.map((j) => (
                  <tr key={j.jours} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 4px', fontWeight: 700 }}>{joursEntiers(j.jours)}</td>
                    <td style={cellN}><b>{j.surDalle + j.parties}</b> cmd</td>
                    <td style={cellN}>{j.surDalle ? <span style={age(j.jours * 1440 + 1)}>{j.surDalle}</span> : '—'}</td>
                    <td style={cellN}>{j.parties || '—'}</td>
                    <td style={cellN}>{j.palettes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ margin: '14px 0 6px', fontSize: '0.88rem' }}>Les plus anciennes encore sur dalle</h3>
          {clients.length > 1 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              <button type="button" style={puce(clientActif === null)} onClick={() => setClient(null)}>Tous les clients</button>
              {clients.map((c) => <button key={c.cle} type="button" style={puce(clientActif === c.cle)} onClick={() => setClient(c.cle)}>{c.client}</button>)}
            </div>
          )}
          {liste.length === 0 ? <p style={{ fontSize: '0.8rem', color: '#64748b' }}>Rien sur la dalle.</p> : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead><tr style={{ color: '#64748b', fontSize: '0.7rem' }}>
                  <th style={th}>Commande</th><th style={th}>Client</th><th style={th}>Prête le</th><th style={thN}>Sur dalle</th><th style={thN}>Depuis</th>
                </tr></thead>
                <tbody>
                  {liste.map((l) => (
                    <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 4px', overflowWrap: 'anywhere' }}><b>{l.orderRef}</b></td>
                      <td style={{ padding: '6px 4px' }}>{l.client}</td>
                      <td style={{ padding: '6px 4px' }}>{dateHeureBxl(l.pret, { annee: true })}</td>
                      <td style={cellN}>{texteRestant(l)}</td>
                      <td style={cellN}><span style={age(l.minutes)}>{joursTexte(l.minutes)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 style={{ margin: '14px 0 6px', fontSize: '0.88rem' }}>Par client</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead><tr style={{ color: '#64748b', fontSize: '0.7rem' }}>
              <th style={th}>Client</th><th style={thN}>Cmd sur dalle</th><th style={thN}>Palettes</th><th style={thN}>Plus ancienne</th><th style={thN}>Médiane avant départ</th><th style={thN}>≥ 7 j</th>
            </tr></thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.cle} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 4px' }}>{c.client}</td>
                  <td style={cellN}>{c.surDalle}</td><td style={cellN}>{c.palettes}</td>
                  <td style={cellN}>{joursTexte(c.plusAncienneMinutes)}</td><td style={cellN}>{joursTexte(c.medianeMinutes)}</td>
                  <td style={cellN}>{c.attention || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '8px 0 0' }}>Palettes × jours encore sur dalle : <b>{String(data.palettesJours).replace('.', ',')}</b> (indicatif — la facturation du stockage applique ses propres règles).</p>

          <button type="button" id="exportDalle" aria-busy={exportEnCours} onClick={exporter}
            style={{ marginTop: 10, border: '1px solid #0f5132', color: '#0f5132', background: '#fff', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer' }}>
            {exportEnCours ? '…' : '⬇ Exporter Excel'}
          </button>
        </>
      )}
    </aside>
  );
}
